import {ReactElement} from 'react'
import {Button, WithStyles, withStyles} from '@material-ui/core'
import {accountTypes, companies, ICompanyEnum} from '@bdswiss/common-enums'
import OpenInNewOutlined from '@material-ui/icons/OpenInNewOutlined'
import {Trans, withNamespaces, WithNamespaces} from 'react-i18next'
import {useHistory} from 'react-router-dom'
import get from 'lodash/get'

import {Account, PammTauroMarketsAccount, PammTauroMarketsMauritiusAccount, PammTauroMarketsMt5Account} from '../../../../../../../graphql/types'
import Amount from '../../../../../../Common/Amount'
import messages from '../../../../../../../assets/messages'
import {getMetadataFromAccount} from '../../../../../AccountCard/utils'
import {DepositNeededBadge} from '../badges/DepositNeededBadge'
import {PendingApprovalBadge} from '../badges/PendingApprovalBadge'
import {AdditionalDepositNeededBadge} from '../badges/AdditionalDepositNeeded'
import {MoneyManagerInfoBadge} from '../badges/MoneyManagerInfoBadge'
import {OfferDetails} from './OfferDetails'
import {styles} from './styles'

const ACCOUNT_TYPE_COMPANY_MAP = {
  [companies.tauroMarkets.value]: accountTypes.PAMMTauroMarketsAccount,
  [companies.tauroMarketsMauritius.value]: accountTypes.PAMMTauroMarketsMauritiusAccount,
}

type PammInvestorAccount = PammTauroMarketsAccount | PammTauroMarketsMauritiusAccount | PammTauroMarketsMt5Account

type PammInvestorAccountCardProps = WithStyles & WithNamespaces & {
  isDepositNeeded: boolean,
  isUnapproved: boolean,
  account: PammInvestorAccount,
  restAccounts: Account[],
  company: ICompanyEnum,
  onClick: () => void
}

function PammInvestorAccountCardBase(props: PammInvestorAccountCardProps): ReactElement {
  const {
    classes,
    account,
    restAccounts,
    company,
    isDepositNeeded,
    isUnapproved,
    onClick,
    lng
  } = props
  const history = useHistory()

  const moneyManagerName = get(account, 'moneyManagerInfo.name')
  const accountType = account.__typename ? accountTypes[account.__typename] : ACCOUNT_TYPE_COMPANY_MAP[company.value]
  const accountMetadata = getMetadataFromAccount(account)
  const relatedForexAccountId = accountMetadata.relatedForexAccountId
  const relatedForexAccount = relatedForexAccountId
    ? restAccounts.find(({id}) => id === relatedForexAccountId)
    : null

  const accountBalance = account.balance ?? 0
  // Already the shortfall the next deposit has to meet, net of `accountBalance` (PT-5773/PT-5774) —
  // the linked trading account's balance does not count towards it and must not be netted off here.
  const minimumDeposit = account.minimumDeposit ?? 0

  const hanldeViewClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation()

    if (!relatedForexAccount?.id) return

    history.push(`/accounts/${relatedForexAccount.id}`)
  }

  return (
    <div className={classes.container} data-testid='pamm-tauro-markets-account-card' onClick={onClick}>
      <div className={classes.icon}>{accountType.shortLabel}</div>
      <div className={classes.content}>

        <div className={classes.contentTitle}>
          <div className={classes.title}>
            {accountType.label}
          </div>
          <Button variant='contained' size='small' color='primary' className={classes.openButton}>
            <Trans {...messages.openPammPortal}/>
            <OpenInNewOutlined className={classes.openIcon}/>
          </Button>
        </div>

        <div className={classes.contentSubTitle}>

          <Amount
            currency={account.currency}
            locale={lng || 'en'}
            value={accountBalance}
          />
          <div className={classes.status}>
            {isDepositNeeded && accountBalance === 0 && (
              <DepositNeededBadge minimumDeposit={minimumDeposit} currency={account.currency} />
            )}
            {isUnapproved && <PendingApprovalBadge classNames={{background: classes.pendingBackground, text: classes.pendingText}}/>}
            {isDepositNeeded && accountBalance > 0 ? (
              <AdditionalDepositNeededBadge additionalDeposit={minimumDeposit} currency={account.currency} />
            ) : null}
            {moneyManagerName ? <MoneyManagerInfoBadge name={moneyManagerName} /> : null}
          </div>
        </div>

        {account.offer ? <OfferDetails offer={account.offer} classes={classes} /> : null}

        {relatedForexAccount ? (
          <div className={classes.footer}>
            <div className={classes.underline}/>
            <div className={classes.relatedAccountContainer}>
              <Trans {...messages.linkedTradingAccount} />
              <div className={classes.relatedAccountBalance}>
                <Amount
                  value={relatedForexAccount?.balance ?? 0}
                  currency={relatedForexAccount?.currency}
                  locale={lng || 'en'}
                />
              </div>
              <div className={classes.relatedAccountLink} onClick={hanldeViewClick}>
                <Trans {...messages.view} />
              </div>
            </div>
          </div>
        ) : null}

      </div>
    </div>
  )
}

const PammInvestorAccountCard = withStyles(styles)(withNamespaces()(PammInvestorAccountCardBase))
export {PammInvestorAccountCard}
